import { css } from '@emotion/react'
import emotionStyled from '@emotion/styled'
import { IconButton, Slide } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { useCallback, useEffect, useRef, useState } from 'react'
import useOutsideClick from '../../../hooks/useOutsideClick'
import { TypoTitle } from './account-biodata/account-biodata-styled'

const StyledModal = emotionStyled.div`
  position: fixed;
  inset: 0px;
  z-index: ${({ zIndex }) => (zIndex ? zIndex.toString() : '101')};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  outline: 2px solid transparent;
  outline-offset: 2px;
  &:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
  }
  background-color: rgb(38 38 38 / 0.7);
`

const StyledModalWrapper = emotionStyled.div`
  position: relative;
  width: 450px;
  height: auto;
  margin-left: auto;
  margin-right: auto;
  margin-top: 1.5rem/* 24px */;
  margin-bottom: 1.5rem/* 24px */;
  overflow: hidden;
  ${({ fullWidth }) => {
    if (fullWidth) {
      return css`
        width: 100% !important;
        height: 100% !important;
      `
    }
  }}
  ${({ fitContent }) => {
    if (fitContent) {
      return css`
        width: fit-content !important;
        height: fit-content !important;
      `
    }
  }}
`

const StyledModalRelative = emotionStyled.div`
  position: relative;
  width: 100%;
  height: 100%;
  flex-direction: column;
  overflow: hidden;
  background-color: #FFF;
  border-radius: 0.5rem/* 8px */;
  outline: 2px solid transparent;
  outline-offset: 2px;
`

const StyledModalHeader = emotionStyled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem/* 12px */;
  padding: 0.75rem/* 12px */;
  border-top-left-radius: 0.25rem/* 4px */;
  border-top-right-radius: 0.25rem/* 4px */;
  position: relative;
  border-bottom-width: 1px;
  box-shadow: 0 4px 2px -2px #ededed;
`

export const ModalBody = emotionStyled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: .8rem;
  flex: 1 1 auto;
  padding: 0.75rem/* 12px */;
  overflow: auto;
  min-height: 200px;
  max-height: 500px;
  &::-webkit-scrollbar {
    background: transparent;
    width: 0.3rem;
    height: 0.3rem;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(21, 21, 21, 0.3);
    border-radius: 1000px;
  }
`

export const ModalFooter = emotionStyled.div`
  position: relative;
  display: flex;
  flex: 1 1 auto;
  padding: 0.75rem/* 12px */;
`

export const ModalHeader = ({ text, handleClose, children, ...props }) => {
  return (
    <StyledModalHeader>
      {text ? <TypoTitle>{text}</TypoTitle> : <TypoTitle>{children}</TypoTitle>}
      <IconButton onClick={handleClose} size='small'>
        <Close fontSize='inherit' />
      </IconButton>
    </StyledModalHeader>
  )
}

const Modal = ({
  open = false,
  onClose,
  disableOutsideClick = false,
  disabled,
  children,
  header = {},
  fullWidth = false,
  fitContent = false,
  zIndex,
  ...props
}) => {
  const [showModal, setShowModal] = useState(open)

  useEffect(() => {
    setShowModal(open)
  }, [open])

  const handleClose = useCallback(() => {
    if (disabled) return
    setShowModal(false)
    setTimeout(() => {
      onClose()
    }, 300)
  }, [disabled, onClose])

  const modalRef = useRef()
  useOutsideClick(modalRef, !disableOutsideClick ? handleClose : null)

  useEffect(() => {
    const bodyEl = document.querySelector('body')
    const isNoScroll = bodyEl.classList.contains('body--no-scroll')
    if (showModal) {
      if (!isNoScroll) {
        bodyEl.classList.add('body--no-scroll')
      }
      return () => {
        bodyEl.classList.remove('body--no-scroll')
      }
    }
    return () => {
      bodyEl.classList.remove('body--no-scroll')
    }
  }, [showModal])

  if (!showModal) {
    return null
  }

  return (
    <StyledModal zIndex={zIndex}>
      <StyledModalWrapper fullWidth={fullWidth} fitContent={fitContent}>
        <Slide direction='up' in={showModal} mountOnEnter unmountOnExit>
          <StyledModalRelative ref={modalRef}>
            <ModalHeader {...header} handleClose={handleClose}>
              {header?.text}
            </ModalHeader>
            {children && children}
          </StyledModalRelative>
        </Slide>
      </StyledModalWrapper>
    </StyledModal>
  )
}

export default Modal
